import {
    Order,
    DataType,
    RequestSubtypes,
    RequestSubtypeNames,
    RequestTypes,
    RequestTypeNames,
    RestrictionLimitedPeriodTKey,
    RollingPeriodUnit,
    RuleSetType,
    JobTransferValidationType,
    VtoCoverageRequiredEdgeNames,
    TimePeriodRatioOption,
    TimePeriodRatioOptionNames,
    TorCreationType, 
    TorCreationTypeNames
} from '../';
import { AwardingRuleParameterType } from '../AwardingRuleParameterType';
import i18n from '../../../i18n';

import _ from 'lodash';

/**
 * @typedef {typeof import('../TimePeriodRatioOption').TimePeriodRatioOption} ITimePeriodRationOption
 * @typedef {typeof import('../RequestType').RequestTypes} IRequestTypes
 * @typedef {{ value: number; label: string }} ListType
 */


/**
 * @param {Order} order
 * @returns {string}
 */
export const getOrderLabel = (order) => {
    switch (order) {
        case Order.Ascending:
            return i18n.t('employmentBillboard:common.sortType.ascending');
        case Order.Descending:
            return i18n.t('employmentBillboard:common.sortType.descending');
        default:
            return '';
    }
};

/**
 * @param {DataType} dataviewFieldDataType
 * @returns {string}
 */
export const getDataTypeLabel = (dataviewFieldDataType) => {
    switch (dataviewFieldDataType) {
        case DataType.Numeric:
            return i18n.t('employmentBillboard:common.dataType.numeric');
        case DataType.Date:
            return i18n.t('employmentBillboard:common.dataType.date');
        default:
            return '';
    }
};

const tKeyPrefixRequestType = 'employmentBillboard:requestType';

/**
 * @param {IRequestTypes[keyof IRequestTypes]} requestType
 * @returns {string} The translated `request type` label
 */
export const getRequestTypeLabel = (requestType) =>
    _.isNil(requestType) ? '' : i18n.t(`${tKeyPrefixRequestType}.${RequestTypeNames[requestType]}`);


/**
 * @param {Object<string, value>} defaultValue
 * @returns {ListType[]}
 */
export const getRequestTypeList = () => Object
    .values(RequestTypes)
    .map((requestType) => ({
        label: i18n.t(`${tKeyPrefixRequestType}.${RequestTypeNames[requestType]}`),
        value: requestType,
    }));

/**
 * @typedef {typeof RequestSubTypes} IRequestSubTypes
 * @param {IRequestTypes[keyof IRequestTypes]} requestType
 * @param {IRequestSubTypes[keyof IRequestSubTypes]} requestSubtype
 * @returns {string} The translated `request type`
 */
export const getRequestSubTypeLabel = (requestType, requestSubtype) =>
    (requestSubtype != null && requestType != null) ? i18n.t(`${tKeyPrefixRequestType}.requestSubtypes.${RequestTypeNames[requestType]}.${RequestSubtypeNames[requestSubtype]}`): null;

/**
 * @typedef {typeof RequestSubTypes} IRequestSubTypes
 * @param {IRequestSubTypes[keyof IRequestSubTypes]} requestType
 * @returns {string} The translated `request subtype`
 */
export const getRequestSubTypeList = (requestType) => Object
    .values(RequestSubtypes)
    .map((requestSubType) => ({
        label: getRequestSubTypeLabel(requestType, requestSubType),
        value: requestSubType,
    }));

/**
 * @param {RuleSetType} ruleType
 * @returns {string}
 */
export const getRuleSetTypeLabel = (ruleType) => {
    switch (ruleType) {
        case RuleSetType.AwardingRule:
            return i18n.t('employmentBillboard:settings.ruleSet.informationStep.awardingRule');
        case RuleSetType.ValidationRule:
            return i18n.t('employmentBillboard:settings.ruleSet.informationStep.validationRule');
        case RuleSetType.BreakRule:
            return i18n.t('employmentBillboard:settings.ruleSet.informationStep.breakRule');
        default:
            return '';
    };
}

/**
 * @param {bool} isActive
 * @returns {string}
 */
 export const getRuleSetActiveLabel = (isActive) => {
    if (isActive) {
        return i18n.t('employmentBillboard:settings.ruleSet.informationStep.activeRuleSection.active');
    }
    return i18n.t('employmentBillboard:settings.ruleSet.informationStep.activeRuleSection.inactive');
}

/**
 * @param {AwardingRuleParameterType} ruleType
 * @returns {string}
 */
export const getAwardingRuleParameterTypeLabel = (ruleType) => {
    switch (ruleType) {
        case AwardingRuleParameterType.CustomDataType:
            return i18n.t('employmentBillboard:settings.ruleSet.sortingRuleStep.customDataType');
        case AwardingRuleParameterType.HyperfindBelonging:
            return i18n.t('employmentBillboard:settings.ruleSet.sortingRuleStep.hyperfindBelonging');
        case AwardingRuleParameterType.DataviewData:
            return i18n.t('employmentBillboard:settings.ruleSet.sortingRuleStep.dataviewData');
        // case AwardingRuleParameterType.JobTransfer:
        //     return i18n.t('employmentBillboard:settings.ruleSet.sortingRuleStep.jobTransfer');
        // case AwardingRuleParameterType.HireDate:
        //     return i18n.t('employmentBillboard:settings.ruleSet.sortingRuleStep.hireDate');
        case AwardingRuleParameterType.BySubmittedRequestDateTime:
            return i18n.t('employmentBillboard:settings.ruleSet.sortingRuleStep.bySubmittedRequestDateTime');
        default:
            return '';
    };
}
/**
 * @param {JobTransferTypeOptions} ruleType
 * @returns {string}
 */
export const getJobTransferValidationTypeLabel = (ruleType) => {
    switch (ruleType) {
        case JobTransferValidationType.PrimaryJobOnly:
            return i18n.t('employmentBillboard:settings.ruleSet.employeeRulesStep.primaryJobOnly');
        case JobTransferValidationType.PrimaryJobAndTransferSet:
            return i18n.t('employmentBillboard:settings.ruleSet.employeeRulesStep.primaryJobAndTransferSet');
        default:
            return '';
    };
}

/** @param {VtoCoverageRequiredEdge} vtoCoverageRequiredEdge */
export const getVtoCoverageRequiredEdgeNames = (vtoCoverageRequiredEdge) => i18n.t(
    `employmentBillboard:form.vtoCoverageRequiredEdges.${VtoCoverageRequiredEdgeNames[vtoCoverageRequiredEdge]}`,
);

/**
 * @param {RollingPeriodUnit} unit
 * @returns {string}
 */
export const getRollingPeriodUnitLabel = (unit) => {
    switch (unit) {
        case RollingPeriodUnit.Hours:
            return i18n.t('employmentBillboard:settings.ruleSet.scheduleRulesStep.rollingPeriodRulesSummary.hour');
        case RollingPeriodUnit.Days:
            return i18n.t('employmentBillboard:settings.ruleSet.scheduleRulesStep.rollingPeriodRulesSummary.day');
        default:
            return '';
    }
};

/**
 * @param {ITimePeriodRationOption} timePeriodRatioOption 
 */
export const getTimePeriodRatioOptionsLabel = (timePeriodRatioOption) =>
    _.isNil(timePeriodRatioOption) ? '' : i18n.t(`employmentBillboard:form.coverageOptions.${TimePeriodRatioOptionNames[timePeriodRatioOption]}`);

/**
 * @param {Object<string, value>} defaultValue
 * @returns {ListType[]}
 */
export const getTimePeriodRatioList = () => Object
    .values(TimePeriodRatioOption)
    .map((timePeriodOption) => ({
        label: getTimePeriodRatioOptionsLabel(timePeriodOption),
        value: timePeriodOption,
    }))

/**
 * 
 * @param {RestrictionLimitedPeriod} period 
 * @returns {string}
 */
export const getRestrictionLimitedPeriodLabel = (period) =>
    i18n.t(`employmentBillboard:settings.ruleSet.employeeRulesStep.onRestrictionRule.restrictionMaxNumberOfRequests.${RestrictionLimitedPeriodTKey[period]}`);

/**
 * @returns {ListType[]}
 */
export const getTorCreationTypeOptions = () => Object
    .keys(TorCreationType)
    .map((key) => ({
        value: TorCreationType[key],
        label: i18n.t(`employmentBillboard:form.torCreation.${key.toLowerCase()}`),
    }));